import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AccountBrand, SearchBrandItem, BrandDetail } from "@/screens/analysis/types";
import { useState, useEffect, useRef } from "react";
import { RiInformationLine, RiDownload2Line } from "@remixicon/react";
import { LuLoaderCircle } from "react-icons/lu";
import { Checkbox } from "@/components/ui/checkbox";
import { SqButton } from "@/components/squircle-ui/button";
import { Separator } from "@/components/ui/separator";
import {
  syncBrandsWithExtension,
  checkExtensionInstalled,
  EXTENSION_ID,
} from "@/utils/brandSync";
import { addBrand } from "@/screens/analysis/apiService";

interface ImportExternalBrandDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  privateBrands: (AccountBrand | SearchBrandItem)[];
  onImport: (addedBrands: AccountBrand[]) => void;
  onCancel: () => void;
  accountId?: string;
}

export function ImportExternalBrandDialog({
  isOpen,
  onOpenChange,
  privateBrands,
  onImport,
  onCancel,
  accountId,
}: ImportExternalBrandDialogProps) {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const [isCheckingExtension, setIsCheckingExtension] = useState(true);
  const [shouldPoll, setShouldPoll] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [checkedBrandIds, setCheckedBrandIds] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);

  // Helper to get consistent brand ID
  const getBrandId = (brand: AccountBrand | SearchBrandItem, index: number): string => {
    // SearchBrandItem has page_name, AccountBrand has sync_status
    if ("page_name" in brand) {
      return brand.page_id;
    }
    // This is AccountBrand
    return brand.id || `brand-${index}`;
  };

  // Initialize all brands as checked when dialog opens
  useEffect(() => {
    if (isOpen) {
      const allBrandIds = new Set(
        privateBrands.map((brand, index) => getBrandId(brand, index)),
      );
      setCheckedBrandIds(allBrandIds);
    }
  }, [isOpen, privateBrands]);

  useEffect(() => {
    if (isOpen) {
      setIsCheckingExtension(true);
      checkExtensionInstalled(EXTENSION_ID)
        .then((installed) => {
          setIsExtensionInstalled(installed);
          setIsCheckingExtension(false);
        })
        .catch(() => {
          setIsExtensionInstalled(false);
          setIsCheckingExtension(false);
        });
    }
  }, [isOpen]);

  // Polling effect: check extension every 5 seconds when shouldPoll is true
  useEffect(() => {
    // Clear any existing interval first to prevent duplicates
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (shouldPoll && !isExtensionInstalled && isOpen) {
      // Start polling every 5 seconds
      pollingIntervalRef.current = setInterval(() => {
        checkExtensionInstalled(EXTENSION_ID)
          .then((installed) => {
            if (installed) {
              setIsExtensionInstalled(true);
              setIsCheckingExtension(false);
              setShouldPoll(false);
              // Stop polling once extension is detected
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            }
          })
          .catch(() => {
            // Continue polling on error
          });
      }, 5000);
    }

    // Cleanup on unmount or when conditions change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [shouldPoll, isExtensionInstalled, isOpen]);

  const handleBrandToggle = (brandId: string) => {
    setCheckedBrandIds((prev) => {
      const next = new Set(prev);
      if (next.has(brandId)) {
        next.delete(brandId);
      } else {
        next.add(brandId);
      }
      return next;
    });
  };

  const handleInstallExtension = () => {
    // Open Chrome Web Store page for the extension
    window.open(
      `https://chromewebstore.google.com/detail/getcrux/${EXTENSION_ID}`,
      "_blank",
    );

    // Start polling to check if extension gets installed
    setShouldPoll(true);
  };

  const getBrandName = (brand: AccountBrand | SearchBrandItem): string => {
    if ("page_name" in brand) {
      return brand.page_name;
    }
    return brand.name;
  };

  const getBrandImage = (brand: AccountBrand | SearchBrandItem): string | null => {
    if ("profile_picture_url" in brand) {
      return brand.profile_picture_url;
    }
    return brand.profile_image_url;
  };

  const handleImportFromAdLibrary = async () => {
    if (!accountId) {
      return;
    }

    // Get checked brands
    const checkedBrands = privateBrands.filter((brand, index) => {
      const brandId = getBrandId(brand, index);
      return checkedBrandIds.has(brandId);
    });

    if (checkedBrands.length === 0) {
      return;
    }

    // Create brand details for API call
    const brandDetails: BrandDetail[] = checkedBrands.map((brand) => {
      if ("page_name" in brand) {
        // SearchBrandItem
        return {
          page_url: brand.page_url,
          brand_platform: "FACEBOOK",
          account_id: accountId,
          page_id: brand.page_id,
          page_name: brand.page_name,
          profile_picture_url: brand.profile_picture_url,
          ad_library_url: brand.ad_library_url,
          category: brand.category || undefined,
        };
      } else {
        // AccountBrand
        return {
          page_url: brand.url,
          ad_library_url: brand.ad_library_url,
          brand_platform: "FACEBOOK",
          account_id: accountId,
          page_id: brand.page_id,
          page_name: brand.name,
          profile_picture_url: brand.profile_image_url || undefined,
        };
      }
    });

    setIsImporting(true);

    try {
      // Call addBrand API with addPrivateBrands: true
      const response = await addBrand({
        accountId,
        brandDetails,
        addPrivateBrands: true,
      });

      // Collect successfully added brands for syncing
      const addedBrands: AccountBrand[] = [];
      response.results.forEach((result) => {
        if (result.status === "success" && result.brand) {
          addedBrands.push(result.brand);
        }
      });

      // Sync added brands with extension
      if (addedBrands.length > 0) {
        // Call the onImport callback with added brands
        onImport(addedBrands);
        void syncBrandsWithExtension(addedBrands, accountId, true);
      }
    } catch (error) {
      console.error("Failed to add private brands:", error);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="p-[20px] w-full max-w-[592px] flex flex-col gap-[18px] border-none rounded-[16px] squircle-mask squircle-radius-[16px] squircle-smooth-[1]"
      >
        {/* Extension Banner */}
        {!isCheckingExtension && !isExtensionInstalled && (
          <div className="flex h-[38px] p-1 justify-between items-center rounded-[12px] bg-[#E7F3FF]">
            <div className="flex items-center gap-2">
              <div className="h-[30px] w-[30px] flex items-center justify-center squircle-mask squircle-radius-[8px] squircle-smooth-[1] bg-white">
                <RiInformationLine size={18} className="text-[#2A94F4] flex-shrink-0" />
              </div>
              <p className="text-[#0071D8] text-[12px] font-medium leading-[140%]">
                Extension required to import. Install it to continue.
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={handleInstallExtension}
              className="flex items-center gap-[6px] rounded-[8px] border border-[#C6E3FF] bg-white shadow-sm h-[30px] px-[14px] py-[8px]"
            >
              <RiDownload2Line size={14} className="shrink-0 text-brand-primary-400" />
              <span className="text-[11px] font-medium text-brand-primary-600">
                Install Extension
              </span>
            </Button>
          </div>
        )}

        {/* Header Content */}
        <div className="flex flex-col">
          <h2 className="text-[14px] font-medium text-brand-primary-new leading-[18px]">
            Some brands couldn’t be added
          </h2>
          <p className="text-[13px] text-brand-primary-600 leading-[18px]">
            Some selections are private, Facebook login required in Meta Ad Library. If
            already logged in, click Import.
          </p>
        </div>

        {/* Brands List */}
        <div className="flex flex-col gap-2 mt-1">
          {privateBrands.map((brand, index) => {
            const brandName = getBrandName(brand);
            const brandImage = getBrandImage(brand);
            const brandId = getBrandId(brand, index);
            const isChecked = checkedBrandIds.has(brandId);

            return (
              <div
                key={brandId}
                className="h-[32px] flex items-center gap-[6px] px-2 py-[6px] rounded-[8px] bg-white squircle squircle-radius-[8px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray"
              >
                <Checkbox
                  className="w-[12px] h-[12px]"
                  checked={isChecked}
                  onCheckedChange={() => handleBrandToggle(brandId)}
                />

                <div className="w-[20px] h-[20px] rounded-[4px] overflow-hidden flex-shrink-0">
                  {brandImage ? (
                    <img
                      src={brandImage}
                      alt={brandName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-brand-primary-800">
                        {brandName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <span className="mt-[2px] text-[12px] font-medium text-brand-primary-800 leading-[20px]">
                  {brandName}
                </span>
              </div>
            );
          })}
        </div>
        {/* Action Buttons */}
        <div className="mt-[6px] w-full flex items-center justify-end gap-2 pt-2">
          <SqButton variant="outline" size="md" onClick={onCancel}>
            Cancel
          </SqButton>
          <SqButton
            variant="default"
            size="md"
            onClick={() => void handleImportFromAdLibrary()}
            disabled={
              checkedBrandIds.size === 0 ||
              !isExtensionInstalled ||
              isCheckingExtension ||
              isImporting
            }
          >
            {isImporting ? (
              <LuLoaderCircle className="animate-spin" size={16} />
            ) : (
              "Import from Ad Library"
            )}
          </SqButton>
        </div>
        <Separator className="absolute bottom-12 left-0 right-0 my-[18px] w-full h-[1px] bg-border-gray" />
      </DialogContent>
    </Dialog>
  );
}
